import type { GeoData } from './GeolocationService';
import FingerprintService from './FingerprintService';

import getApiUrl from '../env';
import type { TTemplateData } from '../components/PublicTemplatePreview/type';
import UtilsService, { type AdvertisingData } from './UtilsService';
import type { ICXCompanyConfiguration } from '../types/util-types';
import RegistrationService from './RegistrationService';
import { trackError } from '../utils/error.utils';

export interface RegistrationData {
    vendor_email?: string;
    vendor_name?: string;
    registration_variant:
        | 'paywall'
        | 'homepage'
        | 'homepage_facebook'
        | 'community';
    geo_data?: GeoData;
    fullName?: string;
    is_sso: boolean;
    sso_type?: string;
    auth_token?: string;
    auth_code?: string;
    profile_img_url?: string;
    pricingPlan?: unknown; // is it boolean?
}

export interface SignupUserResponse {
    api_version: string;
    authentication_token: string;
    _id: string;
}

export interface MarketingAiResponse {
    company_type: string;
    company_type_confidence: string;
    intent: string;
    intent_confidence: string;
}

export interface InvoiceGeneratorResponse {
    secure_url: string;
}
export interface LeadJourneyEvent {
    name: string;
    metadata?: Record<string, any>;
}

export interface TrackLeadJourneyRequest {
    email: string;
    journeyType: string;
    event: LeadJourneyEvent;
    utm?: {} | AdvertisingData;
}

export interface ILeadCreatedParams {
    email: string;
    stable_id: string;
    source: string;
    utms?: Record<string, any>;
    additional_data: Record<string, any>;
}

export default class ApiService {
    private static readonly MAX_TRY_COUNT_FOR_REDIRECT_TO_CLIENT_PORTAL_CONFIGURATION = 3;

    static _apiFetch = async <T>(
        key: RequestInfo,
        data: RequestInit & { isJsonp?: boolean },
        skipFingerprint = false
    ): Promise<T> => {
        const fingerprint = await FingerprintService.getFingerprint();
        const headers = { ...data.headers };
        if (fingerprint && !skipFingerprint) {
            headers['Hb-Api-Fingerprint'] = fingerprint;
        }

        return fetch(key, { ...data, headers }).then(async response => {
            if (response.status === 204) return '';
            if (!response.ok) {
                const err = await response.json();
                throw new Error(err.error);
            }
            if (data.isJsonp) {
                const responseText = await response.text();
                return this.extractJsonFromJsonP(responseText);
            }
            return response.json();
        });
    };

    static ssoSignupUser = (
        ssoData: Record<string, unknown>
    ): Promise<SignupUserResponse> => {
        return this._apiFetch(
            `${getApiUrl()}/api/v2/users/oauth/sso_signup_user`,
            {
                method: 'POST',
                body: JSON.stringify(ssoData),
                headers: {
                    'Content-Type': 'application/json'
                }
            }
        ) as Promise<SignupUserResponse>;
    };

    static generateIntentMarketingAi(
        message: string
    ): Promise<MarketingAiResponse> {
        const stable_id = UtilsService.handleHBAnonymousId();
        const utmParams = RegistrationService.getUtms() || {};

        return this._apiFetch(
            `${getApiUrl()}/api/v2/marketing_ai/intent_generate`,
            {
                method: 'POST',
                body: JSON.stringify({
                    message,
                    stable_id,
                    meta_data: utmParams
                }),
                headers: {
                    'Content-Type': 'application/json'
                }
            }
        )
            .then((response: unknown) => {
                // Optionally validate response shape here
                return response as MarketingAiResponse;
            })
            .catch(error => {
                trackError(error, {
                    message: 'Failed to generate intent'
                });
                throw error;
            });
    }

    static async trackLeadJourney({
        email,
        journeyType,
        event,
        utm
    }: TrackLeadJourneyRequest): Promise<void> {
        try {
            const stable_id = UtilsService.handleHBAnonymousId();
            const hasUtm = utm && Object.keys(utm).length > 0;

            const page = UtilsService.isBrowser()
                ? window.location.pathname || '/'
                : '/';

            const payload: Record<string, any> = {
                email,
                journey_type: journeyType,
                page,
                stable_id,
                event
            };

            if (hasUtm) {
                payload.utm = utm;
            }

            await this._apiFetch(`${getApiUrl()}/api/marketing/lead_journey`, {
                method: 'PUT',
                body: JSON.stringify(payload),
                keepalive: true,
                headers: {
                    'Content-Type': 'application/json'
                }
            });
        } catch (error) {
            trackError(error, {
                message: '[ApiService] Failed to track lead journey',
                email,
                journeyType,
                event: event.name
            });
        }
    }

    static createEmailSignature = data => {
        interface Res {
            success?: boolean;
            lead_email_signature_id?: string;
            error?: boolean;
            error_message?: string;
            error_type?: string;
            is_timeout?: boolean;
            note?: string;
        }

        return this._apiFetch(
            `${getApiUrl()}/api/v2/lead_email_signature_generator`,
            {
                method: 'POST',
                body: JSON.stringify(data),
                headers: {
                    'Content-Type': 'application/json'
                }
            }
        ).then((res: Res) => {
            if (res.error) {
                throw new Error(res.error_message);
            } else {
                return res;
            }
        });
    };

    static getEmailSignatureById(id: string) {
        if (!/^[a-f0-9]{24}$/.test(id)) {
            return Promise.reject(new Error('invalid email signature id'));
        }
        const url =
            'https://s3.amazonaws.com/' +
            process.env.S3_email_signature_bucket +
            '/' +
            id +
            '.html';
        return fetch(url, { method: 'GET' }).then(response => {
            if (response.status === 200) {
                return response.text();
            } else {
                throw new Error('failed to fetch email signature');
            }
        });
    }

    static uploadImage = imageData => {
        const url =
            'https://api.cloudinary.com/v1_1/honeybook-landing/image/upload';
        return this._apiFetch(
            url,
            {
                method: 'POST',
                body: imageData
            },
            true
        );
    };

    static async fetchClientPortalConfiguration(
        url: string
    ): Promise<ICXCompanyConfiguration> {
        return this._apiFetch<ICXCompanyConfiguration>(
            url,
            {
                method: 'GET',
                isJsonp: true
            },
            true
        );
    }

    static async handleRedirectTo({
        clientPortalConfiguration,
        tryCount = 0
    }: {
        clientPortalConfiguration: ICXCompanyConfiguration;
        tryCount?: number;
    }) {
        if (
            !clientPortalConfiguration.redirect_to ||
            tryCount >=
                this.MAX_TRY_COUNT_FOR_REDIRECT_TO_CLIENT_PORTAL_CONFIGURATION
        ) {
            return clientPortalConfiguration;
        }
        const { domain, prefix } = clientPortalConfiguration.redirect_to;
        const domainForUrl = UtilsService.getClientPortalConfigurationAwsDomain(
            {
                domain
            }
        );
        const awsBucket = UtilsService.getClientPortalAwsBucket();
        const companyConfigurationJsonpUrl = `https://s3.amazonaws.com/${awsBucket}/${domainForUrl}/${prefix}_configuration.js?xhr=1`;
        clientPortalConfiguration = await this.fetchClientPortalConfiguration(
            companyConfigurationJsonpUrl
        );

        return this.handleRedirectTo({
            clientPortalConfiguration,
            tryCount: tryCount + 1
        });
    }

    static async getClientPortalConfiguration(
        templateId: string
    ): Promise<ICXCompanyConfiguration> {
        // fetching client portal configuration
        const clientPortalAwsDomain =
            UtilsService.getPublicConfigurationAwsDomain();
        let clientPortalConfiguration =
            await this.fetchClientPortalConfiguration(
                `${clientPortalAwsDomain}/ptc/redirect_files/${templateId}.html`
            );

        // when redirect_to is present, we need to fetch the configuration from target aws bucket
        if (clientPortalConfiguration.redirect_to) {
            clientPortalConfiguration = await this.handleRedirectTo({
                clientPortalConfiguration
            });
        }
        return clientPortalConfiguration;
    }

    static async getTemplateData(templateId: string): Promise<TTemplateData> {
        const clientPortalConfiguration =
            await this.getClientPortalConfiguration(templateId);

        // data we need to fetch the public template data from s3
        const { company_id } = clientPortalConfiguration;
        const s3UrlPrefix = UtilsService.getS3UrlPrefixForPublicTemplateData();
        const envName = UtilsService.getEnvNameForS3();
        const s3Url = `${s3UrlPrefix}/exports/public_flows/${envName}/${company_id}/${templateId}.json${
            UtilsService.isDev() ? '?local=true' : ''
        }`;

        // fetching public template data
        return this._apiFetch<TTemplateData>(
            s3Url,
            {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' }
            },
            true
        );
    }

    static async announceLeadCreated(data: ILeadCreatedParams): Promise<void> {
        return this._apiFetch<void>(`${getApiUrl()}/api/lead_created`, {
            method: 'POST',
            body: JSON.stringify(data),
            headers: {
                'Content-Type': 'application/json'
            }
        });
    }

    private static extractJsonFromJsonP(jsonPText: string) {
        // eslint-disable-next-line no-eval
        return eval(
            jsonPText.slice(
                jsonPText.indexOf('('),
                jsonPText.lastIndexOf(')') + 1
            )
        );
    }
}
